/**
 * LabelLayoutSection
 *
 * Card section in Production Settings that launches the full-screen label editor.
 * The editor opens as a full-page overlay for a better design experience.
 *
 * Provides:
 * - Card with description and launch button
 * - Full-screen modal with the LabelLayoutEditor
 * - Proper context providers (TooltipProvider)
 * - Close button and keyboard escape to close
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { LabelLayoutEditor } from './LabelLayoutEditor';
import { LabelLayoutErrorBoundary } from './LabelLayoutErrorBoundary';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Tags, Pencil, X, Monitor } from 'lucide-react';
import { cn } from '@/lib/utils';
import * as FocusScope from '@radix-ui/react-focus-scope';

/**
 * Settings page section for the label layout editor.
 * Shows a card with a button to open the full-screen editor.
 */
export function LabelLayoutSection() {
  const { t } = useTranslation('settings');
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);

  // Handle escape key to close - using functional update to avoid stale closure
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      // Note: EditorToolbar also handles Escape to deselect components first
      // This handler only fires after component deselection or when no component is selected
      setIsEditorOpen(false);
    }
  }, []);

  // Add/remove keyboard listener
  useEffect(() => {
    if (isEditorOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent background scroll when editor overlay is open.
      // The scroll container is the SidebarInset <main>, not the body.
      const inset = document.querySelector<HTMLElement>('[data-sidebar="inset"]');
      if (inset) inset.style.overflow = 'hidden';
    } else {
      const inset = document.querySelector<HTMLElement>('[data-sidebar="inset"]');
      if (inset) inset.style.overflow = '';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const inset = document.querySelector<HTMLElement>('[data-sidebar="inset"]');
      if (inset) inset.style.overflow = '';
    };
  }, [isEditorOpen, handleKeyDown]);

  return (
    <>
      {/* Embedded content (used in LabelTemplatesTab accordion) */}
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">
          {t('production.labelLayout.description')}
          
        </p>
        <Button
          ref={triggerButtonRef}
          onClick={() => setIsEditorOpen(true)}
          className="h-12 gap-2"
        >
          <Pencil className="h-4 w-4" />
          {t('production.labelLayout.openDesigner')}
        </Button>
        {/* Mobile disclaimer - only visible on small screens */}
        <p className="text-xs text-muted-foreground flex items-center gap-1.5 sm:hidden">
          <Monitor className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t('production.labelLayout.mobileDisclaimer')}
        </p>
      </div>

      {/* Full-Screen Editor Overlay */}
      {isEditorOpen && (
        <FocusScope.Root
          trapped
          onMountAutoFocus={(e) => {
            // Prevent default to handle focus ourselves
            e.preventDefault();
          }}
          onUnmountAutoFocus={(e) => {
            // Return focus to trigger button when modal closes
            e.preventDefault();
            triggerButtonRef.current?.focus();
          }}
        >
          <div
            className={cn(
              'fixed inset-0 z-50 bg-background',
              'animate-in fade-in-0 duration-200'
            )}
            role="dialog"
            aria-modal="true"
            aria-label={t('production.labelLayout.editorTitle')}
          >
            {/* Header */}
            <div className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
              <div className="container mx-auto px-6 py-4 max-w-7xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-indigo-500/10">
                      <Tags className="h-5 w-5 text-indigo-600" aria-hidden="true" />
                    </div>
                    <div>
                      <h1 className="text-xl font-semibold">{t('production.labelLayout.editorTitle')}</h1>
                      <p className="text-sm text-muted-foreground">
                        {t('production.labelLayout.editorSubtitle')}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setIsEditorOpen(false)}
                    className="h-11 gap-2"
                  >
                    <X className="h-4 w-4" />
                    {t('production.labelLayout.closeEditor')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Editor Content */}
            <div className="container mx-auto px-6 py-6 max-w-7xl overflow-auto h-[calc(100vh-80px)]">
              <TooltipProvider>
                <LabelLayoutErrorBoundary>
                  <LabelLayoutEditor />
                </LabelLayoutErrorBoundary>
              </TooltipProvider>
            </div>
          </div>
        </FocusScope.Root>
      )}
    </>
  );
}
